// Game tuning. Numbers carried over from the original prototype where they
// were playtested (movement, knockback); round/lava numbers are new because
// the game is now round-based instead of an endless arena.

export const TICK_RATE = 30;
export const DT = 1 / TICK_RATE;

/** Square world; the island sits in the middle, lava everywhere else. */
export const WORLD = { size: 1500, cx: 750, cy: 750 } as const;

export const ISLAND = {
  initialRadius: 600,
  minRadius: 100,
  /** ms after round start before the island begins sinking */
  shrinkDelayMs: 3000,
  /** world units per second (600 → 100 takes ~55 s) */
  shrinkPerSec: 9,
  /** practice mode (lobby with dummies): hold at the minimum this long, then rise back to full */
  practiceHoldMs: 3000,
} as const;

export const ORB = {
  radius: 28,
  /**
   * Walking is Warlock-style: (nearly) instant start and stop at a constant
   * speed, no drag. `accelTime` / `stopTime` are the time constants of the
   * exponential approach to the target velocity — 50 ms is "instant" to the
   * hand but keeps the joystick from feeling twitchy.
   */
  maxSpeed: 320,
  accelTime: 0.03,
  stopTime: 0.03,
  /**
   * Knockback decays separately and slowly: v *= 0.1 ** (dt * friction).
   * 0.7 → e-folding time ≈ 0.6 s, so a hit is a slide you can walk against,
   * not a shove (Warlock's ×0.96 per 35 ms tick ≈ 0.5 here).
   */
  knockbackFriction: 0.7,
  /** velocities below this snap to zero */
  stopThreshold: 1,
  /** orb-vs-orb bounce */
  restitution: 0.7,
  /** even a gentle bump transfers at least this much velocity */
  minBumpImpulse: 120,
  maxHp: 100,
} as const;

export const FIREBALL = {
  radius: 10,
  speed: 600,
  cooldownMs: 3000,
  baseDamage: 25,
  /** how far a fireball flies before fizzling (world units); ~0.87 s at `speed` */
  range: 520,
  /** initial slide speed of a clean hit; scaled by the hit's force (0.6–1.0) and by damage taken */
  knockbackVelocity: 840,
  /** knockback multiplier = 1 + (damage taken / max hp) * this — Warlock's kb_points ramp */
  knockbackDamageScale: 1,
  /** world-unit margin beyond the world edge before a fireball is culled */
  cullMargin: 100,
  /** max one-way latency (ms) the server compensates by advancing a fireball's spawn */
  maxSpawnLeadMs: 150,
} as const;

/** Warlock's teleport: instant, point-targeted, clamped to `range`, keeps some of your slide. */
export const TELEPORT = {
  range: 400,
  cooldownMs: 4500,
  /** fraction of knockback velocity kept through the jump (Warlock: 0.8) */
  keepMomentum: 0.8,
} as const;

/** Shockwave (right click): no damage, hard radial knockback on everyone nearby. */
export const SHOCKWAVE = {
  radius: 150,
  cooldownMs: 6000,
  /** slide speed at the centre; falls off to `edgeFactor` of that at the radius; damage ramp applies */
  knockbackVelocity: 900,
  edgeFactor: 0.55,
} as const;

export const LAVA = {
  damagePerSec: 20,
  speedPenalty: 0.6,
  accelPenalty: 0.5,
} as const;

export const ROUND = {
  minPlayers: 2,
  countdownMs: 4000,
  resultsMs: 6000,
  /** respawn delay while in the lobby (rounds have no respawn) */
  lobbyRespawnMs: 2500,
  winScore: 3,
  hitScore: 1,
} as const;

export const NET = {
  maxNameLength: 20,
  /** client → server input message cap per second (changes + keepalives) */
  maxInputRate: 40,
  /** client re-sends its current input at least every N ticks even if unchanged */
  inputKeepaliveTicks: 10,
  /** no input message for this long → the server treats the player as idle */
  inputStaleMs: 600,
  /** if the client's tick runs ahead of the server's by more than this, the server steps twice to catch up */
  inputAheadSlack: 3,
  /** aim is quantized to this many directions before sending, so mouse jitter doesn't spam messages */
  aimSteps: 64,
  /** client renders remote state this far behind the newest snapshot (ms) */
  interpDelayMs: 50,
} as const;
